/**
 * Utility functions for handling media operations (images and videos) in the chat app
 */

/**
 * Generates the full URL for a media file based on the file path
 * @param {string} filePath - The relative path to the media file
 * @param {string} baseUrl - The base URL of the API server (should be passed from component using config.apiUrl)
 * @returns {string} The full URL to the media file
 */
import config from '../config';

export const getMediaUrl = (filePath, baseUrl = null) => {
  // Use config.apiUrl as default if baseUrl not provided
  if (!baseUrl) {
    baseUrl = config.apiUrl;
  }
  
  if (!filePath) return '';
  
  // If it's already a full URL, return as is
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return filePath;
  }
  
  // Ensure the path starts with /
  const cleanPath = filePath.startsWith('/') ? filePath : `/${filePath}`;
  
  return `${baseUrl}${cleanPath}`;
};

/**
 * Validates if a file is a valid image
 * @param {File} file - The file to validate
 * @returns {boolean} Whether the file is a valid image
 */
export const isValidImage = (file) => {
  if (!file) return false;
  
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  return validTypes.includes(file.type);
};

/**
 * Validates if a file is a valid video
 * @param {File} file - The file to validate
 * @returns {boolean} Whether the file is a valid video
 */
export const isValidVideo = (file) => {
  if (!file) return false;
  
  const validTypes = ['video/mp4', 'video/mpeg', 'video/quicktime', 'video/webm', 'video/avi', 'video/mov'];
  return validTypes.includes(file.type);
};

/**
 * Validates if a file is a valid media file (image or video)
 * @param {File} file - The file to validate
 * @returns {boolean} Whether the file is valid media
 */
export const isValidMedia = (file) => {
  return isValidImage(file) || isValidVideo(file);
};

/**
 * Gets the type of media file
 * @param {File} file - The file to check
 * @returns {string} 'image', 'video', or 'unknown'
 */
export const getMediaType = (file) => {
  if (isValidImage(file)) return 'image';
  if (isValidVideo(file)) return 'video';
  return 'unknown';
};

/**
 * Validates media file size
 * @param {File} file - The file to validate
 * @param {number} maxSizeMB - Maximum size in MB (default: 25MB for videos, 5MB for images)
 * @returns {boolean} Whether the file size is valid
 */
/** Vibe / status upload limits (must match backend/routes/status.js) */
export const VIBE_IMAGE_MAX_MB = 5;
export const VIBE_VIDEO_MAX_MB = 25;

export const getVibeMaxSizeMB = (file) =>
  file?.type?.startsWith('video/') ? VIBE_VIDEO_MAX_MB : VIBE_IMAGE_MAX_MB;

export const validateVibeUploadFile = (file) => {
  if (!file) return { ok: false, message: 'No file selected.' };
  const isVideo = file.type?.startsWith('video/');
  const isImage = file.type?.startsWith('image/');
  if (!isVideo && !isImage) {
    return { ok: false, message: 'Only image and video files are allowed.' };
  }
  if (!isValidMediaSize(file)) {
    const maxMb = getVibeMaxSizeMB(file);
    return {
      ok: false,
      message: `File is too large (${formatFileSize(file.size)}). Maximum ${maxMb}MB for ${isVideo ? 'videos' : 'images'}.`,
    };
  }
  return { ok: true };
};

export const isValidMediaSize = (file, maxSizeMB = null) => {
  if (!file) return false;
  
  // Set default max size based on file type
  if (maxSizeMB === null) {
    maxSizeMB = isValidVideo(file) ? VIBE_VIDEO_MAX_MB : VIBE_IMAGE_MAX_MB;
  }
  
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
};

/**
 * Formats file size for display
 * @param {number} bytes - Size in bytes
 * @returns {string} Formatted size string
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Creates a preview URL for a media file
 * @param {File} file - The file to create preview for
 * @returns {string} The preview URL
 */
export const createMediaPreview = (file) => {
  if (!file || !isValidMedia(file)) return '';
  
  return URL.createObjectURL(file);
};

/**
 * Revokes a preview URL to free up memory
 * @param {string} url - The URL to revoke
 */
export const revokeMediaPreview = (url) => {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
};

/**
 * Gets video duration from a file
 * @param {File} file - The video file
 * @returns {Promise<number>} Duration in seconds
 */
export const getVideoDuration = (file) => {
  return new Promise((resolve, reject) => {
    if (!isValidVideo(file)) {
      reject(new Error('File is not a valid video'));
      return;
    }

    const video = document.createElement('video');
    video.preload = 'metadata';
    
    video.onloadedmetadata = () => {
      resolve(video.duration);
      URL.revokeObjectURL(video.src);
    };
    
    video.onerror = () => {
      reject(new Error('Failed to load video metadata'));
      URL.revokeObjectURL(video.src);
    };
    
    video.src = URL.createObjectURL(file);
  });
};

/**
 * Formats video duration for display
 * @param {number} duration - Duration in seconds
 * @returns {string} Formatted duration (e.g., "1:23", "10:45")
 */
export const formatVideoDuration = (duration) => {
  if (!duration || duration < 0) return '0:00';
  
  const minutes = Math.floor(duration / 60);
  const seconds = Math.floor(duration % 60);
  
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Creates a video thumbnail from a video file
 * @param {File} file - The video file
 * @param {number} seekTime - Time in seconds to capture thumbnail (default: 1)
 * @returns {Promise<string>} Base64 encoded thumbnail image
 */
export const createVideoThumbnail = (file, seekTime = 1) => {
  return new Promise((resolve, reject) => {
    if (!isValidVideo(file)) {
      reject(new Error('File is not a valid video'));
      return;
    }

    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    video.onloadedmetadata = () => {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      video.currentTime = Math.min(seekTime, video.duration);
    };
    
    video.onseeked = () => {
      try {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const thumbnail = canvas.toDataURL('image/jpeg', 0.8);
        resolve(thumbnail);
        URL.revokeObjectURL(video.src);
      } catch (error) {
        reject(new Error('Failed to generate thumbnail: ' + error.message));
        URL.revokeObjectURL(video.src);
      }
    };
    
    video.onerror = () => {
      reject(new Error('Failed to load video for thumbnail'));
      URL.revokeObjectURL(video.src);
    };
    
    video.src = URL.createObjectURL(file);
  });
};
